import * as React from 'react'
import { Box, Chip, alpha } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { DateCalendar, PickerDay } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import clsx from 'clsx'
import { type Dayjs } from 'dayjs'

interface CustomDayProps {
  day: Dayjs
  selectedDays: Dayjs[]
  onDaySelect: (day: Dayjs) => void
}

function CustomDay({
  day,
  selectedDays,
  onDaySelect,
  ...props
}: CustomDayProps): React.ReactElement {
  const isSelected = selectedDays.some((d: Dayjs) => d.isSame(day, 'day'))
  const theme = useTheme()
  return (
    <PickerDay
      isFirstVisibleCell={false}
      isLastVisibleCell={false}
      outsideCurrentMonth={false}
      {...props}
      day={day}
      onDaySelect={() => onDaySelect(day)}
      className={clsx({ 'Mui-selected': isSelected })}
      sx={{
        ...(isSelected && {
          backgroundColor: theme.palette.action.selected,
          color: theme.palette.primary.contrastText,
          '&:hover': { backgroundColor: theme.palette.action.hover },
        }),
      }}
    />
  )
}

interface MultiDatePickerProps {
  selectedDays: Dayjs[]
  setSelectedDays: (days: Dayjs[]) => void
  boxShadow?: boolean
}

export default function MultiDatePicker({
  selectedDays,
  setSelectedDays,
  boxShadow,
}: MultiDatePickerProps): React.ReactElement {
  const theme = useTheme()
  const handleDaySelect = (day: Dayjs): void => {
    const exists = selectedDays.some((d: Dayjs) => d.isSame(day, 'day'))
    const newSelectedDays = exists
      ? selectedDays.filter((d: Dayjs) => !d.isSame(day, 'day'))
      : [...selectedDays, day]
    setSelectedDays(newSelectedDays)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          alignItems: 'center',
          width: '100%',
        }}
      >
        <DateCalendar
          sx={{
            height: 'auto',
            boxShadow: boxShadow ? theme.shadows[1] : 'none',
          }}
          slots={{
            day: (dayProps) => (
              <CustomDay {...dayProps} selectedDays={selectedDays} onDaySelect={handleDaySelect} />
            ),
          }}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            width: '100%',
            gap: 1,
            marginTop: '0.5rem',
          }}
        >
          {selectedDays.map((day: Dayjs) => (
            <Chip
              key={day.format('YYYY-MM-DD')}
              label={day.format('MMM D, YYYY')}
              size='small'
              sx={{
                backgroundColor: alpha(theme.palette.info.light, 0.15),
                color: theme.palette.info.main,
              }}
            />
          ))}
        </Box>
      </Box>
    </LocalizationProvider>
  )
}
